/**
 * Paid upgrade for the free gift pillow inside the cart.
 *
 * There is exactly one switch per free-gift line - never a second one on the
 * paid pillow's own line - so the cart never shows both a standalone upgrade
 * line item and an upgrade control at the same time.
 *
 * The switch itself normally never touches the cart: turning it on only
 * stores a "pending" record (sessionStorage) and bumps the on-screen
 * estimated total, so it feels instant. The pillow is only actually added -
 * and the free gift actually removed - right before the customer proceeds to
 * checkout, handled by the global click interceptor on the checkout buttons
 * at the bottom of this file.
 *
 * If the upgrade pillow is already genuinely in the cart (for example the
 * customer completed this flow earlier and came back, or the gift app
 * re-added the free gift on a later visit), the switch renders already "on"
 * and unchecking it is a real, immediate removal of that real line - undoing
 * something that truly exists in the cart should not be deferred.
 *
 * Rendered by snippets/kik-gift-pillow-upgrade.liquid.
 */
(function () {
  const ATTRIBUTE_NAME = 'pillow_upgrade';
  const ATTRIBUTE_VALUE = 'cooling';
  const PENDING_KEY = 'kikPillowUpgradePending';

  const routes = () => window.routes || {};

  async function postCart(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.status >= 400) {
      throw new Error(data.description || data.message || 'Cart update failed');
    }

    return data;
  }

  function getCartState() {
    return fetch(`${routes().root_url || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then((response) => response.json());
  }

  function readPending() {
    try {
      return JSON.parse(sessionStorage.getItem(PENDING_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function writePending(pending) {
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    } catch (error) {
      // Storage unavailable (private mode, quota, etc). The upgrade simply
      // will not survive a page reload, which is an acceptable fallback.
    }
  }

  function clearAllPending() {
    try {
      sessionStorage.removeItem(PENDING_KEY);
    } catch (error) {
      // no-op
    }
  }

  /**
   * Adds `deltaCents` to the amount found inside `text`, keeping whatever
   * currency symbol/code and thousands/decimal formatting it already has.
   * Avoids re-implementing the shop's money format from scratch.
   */
  function bumpMoneyText(text, deltaCents) {
    if (!deltaCents) return text;

    const match = text.match(/\d[\d.,]*\d|\d/);
    if (!match) return text;

    const raw = match[0];
    const decimalMatch = raw.match(/([.,])(\d{2})$/);
    const decimalSeparator = decimalMatch ? decimalMatch[1] : '.';
    const thousandSeparator = decimalSeparator === '.' ? ',' : '.';

    const normalized = raw.split(thousandSeparator).join('').replace(decimalSeparator, '.');
    const currentCents = Math.round(parseFloat(normalized) * (decimalMatch ? 100 : 1));
    const newCents = Math.max(0, currentCents + deltaCents);
    const newAmount = (newCents / 100).toFixed(2);
    const [integerPart, decimalPart] = newAmount.split('.');
    const withThousands = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);

    return text.replace(raw, `${withThousands}${decimalSeparator}${decimalPart}`);
  }

  class KikGiftPillowUpgrade extends HTMLElement {
    connectedCallback() {
      // Own property, not a data attribute: the theme clones cart markup to keep
      // a previous cart state, and a cloned flag would block re-binding.
      if (this.isBound) return;
      this.isBound = true;

      const input = this.querySelector('[data-gift-pillow-upgrade-action]');
      if (input) input.addEventListener('change', this.handleChange.bind(this));

      if (this.isDeferred) {
        this.hydratePendingState();
      } else {
        // The upgrade is already real; any leftover pending record for the
        // same gift line is stale and would otherwise double-add at checkout.
        this.clearStalePending();
      }
    }

    handleChange(event) {
      const input = event.target;

      if (this.hasAttribute('aria-busy')) {
        // A real network call is already in flight; ignore extra toggles.
        input.checked = !input.checked;
        return;
      }

      if (this.isDeferred) {
        this.togglePending(input.checked);
        return;
      }

      // Not deferred means the upgrade pillow is already real, so this switch
      // can only ever be unchecked here (turning it back on mid-request is
      // guarded above); the only real action left is reverting to the gift.
      if (!input.checked) {
        this.revert();
      }
    }

    get isDeferred() {
      return this.dataset.deferUntilCheckout === 'true';
    }

    get cartController() {
      return (
        this.closest('cart-page, cart-drawer') ||
        document.querySelector('cart-page') ||
        document.querySelector('cart-drawer')
      );
    }

    get lineIndex() {
      return parseInt(this.dataset.lineIndex, 10);
    }

    /** Index of the real upgrade pillow line, only set once it truly exists in the cart. */
    get upgradeLineIndex() {
      return parseInt(this.dataset.upgradeLineIndex, 10);
    }

    get upgradeVariantId() {
      return Number(this.dataset.upgradeVariantId);
    }

    get priceCents() {
      return parseInt(this.dataset.upgradePrice, 10) || 0;
    }

    /** Restores the switch to "on" when the cart still remembers a pending choice. */
    hydratePendingState() {
      const productId = this.dataset.giftProductId;
      if (!productId) return;

      const pending = readPending();
      const entry = pending[productId];
      if (!entry) return;

      entry.quantity = parseInt(this.dataset.quantity, 10) || entry.quantity || 1;
      entry.variantId = this.upgradeVariantId || entry.variantId;
      entry.priceCents = this.priceCents || entry.priceCents || 0;
      pending[productId] = entry;
      writePending(pending);

      this.setChecked(true);
      this.bumpTotal(entry.priceCents);
    }

    /** Drops a leftover pending record once the same upgrade is already real. */
    clearStalePending() {
      const productId = this.dataset.giftProductId;
      if (!productId) return;

      const pending = readPending();
      if (!pending[productId]) return;

      delete pending[productId];
      writePending(pending);
    }

    togglePending(isChecked) {
      const productId = this.dataset.giftProductId;
      if (!productId || !this.upgradeVariantId) return;

      const pending = readPending();

      if (!isChecked) {
        delete pending[productId];
        writePending(pending);
        this.setChecked(false);
        this.bumpTotal(-this.priceCents);
      } else {
        pending[productId] = {
          variantId: this.upgradeVariantId,
          quantity: parseInt(this.dataset.quantity, 10) || 1,
          priceCents: this.priceCents,
        };
        writePending(pending);
        this.setChecked(true);
        this.bumpTotal(this.priceCents);
      }
    }

    setChecked(isChecked) {
      this.classList.toggle('gift-pillow-upgrade--applied', isChecked);
      this.dataset.mode = isChecked ? 'revert' : 'upgrade';

      const trigger = this.querySelector('[data-gift-pillow-upgrade-action]');
      if (!trigger) return;

      trigger.checked = isChecked;
      trigger.setAttribute('aria-checked', isChecked ? 'true' : 'false');
      trigger.setAttribute('aria-label', isChecked ? this.dataset.labelRevert || '' : this.dataset.labelUpgrade || '');
    }

    /** Best-effort preview of the estimated total; the real total is only known at checkout. */
    bumpTotal(deltaCents) {
      if (!deltaCents) return;

      const cart = this.cartController;
      const totalEl = cart && cart.querySelector('[data-cart-total-price]');
      if (!totalEl) return;

      totalEl.textContent = bumpMoneyText(totalEl.textContent, deltaCents);
    }

    /**
     * Real, immediate revert of an upgrade pillow that already exists for
     * real in the cart. This switch lives on the free gift line, which is
     * already present, so there is nothing to wait for - just remove the
     * paid pillow's own line and let the cart re-render.
     */
    async revert() {
      this.setBusy(true);
      this.showError('');

      try {
        await postCart(routes().cart_update_url || '/cart/update.js', {
          attributes: { [ATTRIBUTE_NAME]: '' },
        });

        this.removeLine(this.upgradeLineIndex);
      } catch (error) {
        this.setBusy(false);
        this.setChecked(true);
        this.showError(error.message);
      }
    }

    /** Hands the removal to the theme so it re-renders the cart the usual way. */
    removeLine(index) {
      const cart = this.cartController;
      const targetIndex = typeof index === 'number' && !Number.isNaN(index) ? index : this.lineIndex;

      if (cart && typeof cart.updateItem === 'function' && !Number.isNaN(targetIndex)) {
        cart.updateItem(targetIndex, 0);
        return;
      }

      window.location.reload();
    }

    setBusy(isBusy) {
      this.toggleAttribute('aria-busy', isBusy);

      const trigger = this.querySelector('[data-gift-pillow-upgrade-action]');
      if (trigger) trigger.disabled = isBusy;
    }

    showError(message) {
      const target = this.querySelector('[data-gift-pillow-upgrade-error]');
      if (!target) return;

      target.textContent = message || '';
      target.hidden = !message;
    }
  }

  if (!customElements.get('kik-gift-pillow-upgrade')) {
    customElements.define('kik-gift-pillow-upgrade', KikGiftPillowUpgrade);
  }

  /**
   * Commits every pending pillow upgrade for real: adds the paid variant(s)
   * and removes the matching free gift line(s). Runs once, right before the
   * cart form is allowed to continue on to checkout.
   */
  async function commitPendingUpgrades() {
    const pending = readPending();
    const giftProductIds = Object.keys(pending);
    if (!giftProductIds.length) return;

    const items = giftProductIds
      .map((id) => pending[id])
      .filter((entry) => entry && entry.variantId)
      .map((entry) => ({
        id: entry.variantId,
        quantity: entry.quantity || 1,
        properties: { _gift_upgrade: 'cooling-pillow' },
      }));

    if (items.length) {
      await postCart(routes().cart_add_url || '/cart/add.js', { items });
    }

    await postCart(routes().cart_update_url || '/cart/update.js', {
      attributes: { [ATTRIBUTE_NAME]: ATTRIBUTE_VALUE },
    });

    const cart = await getCartState();
    const removals = cart.items
      .filter((line) => giftProductIds.includes(String(line.product_id)))
      .map((line) => postCart(routes().cart_change_url || '/cart/change.js', { id: line.key, quantity: 0 }));

    await Promise.all(removals);

    clearAllPending();
  }

  /**
   * Checkout buttons submit the cart form directly; this intercepts that one
   * click, settles any pending upgrades first, then lets the same button
   * continue the submission so the rest of the theme's checkout flow (consent
   * validation, loading state, redirect) behaves exactly as before.
   *
   * Express/dynamic checkout buttons (Shop Pay, PayPal, etc.) are rendered
   * and controlled by Shopify outside of this form, so they cannot be
   * intercepted the same way; pending upgrades are only committed when the
   * customer uses the theme's own Checkout button.
   */
  function initCheckoutInterception() {
    document.addEventListener(
      'click',
      function (event) {
        const button = event.target.closest('[data-cart-checkout-button], [data-cart-page-checkout-button]');
        if (!button || button.disabled || button.dataset.pillowUpgradeSubmitting === 'true') return;

        const pending = readPending();
        if (!Object.keys(pending).length) return;

        const form = button.form;
        if (!form) return;

        const scope = button.closest('cart-page, cart-drawer') || document;
        const consentCheckbox = scope.querySelector('.checkbox__input[required]');
        if (consentCheckbox && !consentCheckbox.checked) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        button.dataset.pillowUpgradeSubmitting = 'true';
        button.disabled = true;
        button.classList.add('loading');

        commitPendingUpgrades()
          .then(() => {
            button.disabled = false;
            button.classList.remove('loading');
            delete button.dataset.pillowUpgradeSubmitting;

            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit(button);
              return;
            }

            const submitProxy = document.createElement('input');
            submitProxy.type = 'hidden';
            submitProxy.name = button.name;
            submitProxy.value = button.value || '';
            form.appendChild(submitProxy);
            form.submit();
          })
          .catch(() => {
            button.disabled = false;
            button.classList.remove('loading');
            delete button.dataset.pillowUpgradeSubmitting;
          });
      },
      true
    );
  }

  initCheckoutInterception();
})();
